import { FC, useEffect, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { toast } from 'react-toastify';
import { Button, Input, Spinner } from 'reactstrap';
import GoBack from '../../components/GoBack';
import { STARTER_INTERVIEW_QUESTIONS } from '../../constants';
import { ADMIN_ROUTE } from '../../utils/constant';
import {
  ADD_STARTER_TEMPLATE_QUESTION,
  DELETE_STARTER_TEMPLATE_QUESTION,
  REORDER_STARTER_TEMPLATE_QUESTIONS,
  STARTER_INTERVIEW_QUESTION_TEMPLATE,
  UPDATE_STARTER_TEMPLATE_QUESTION,
} from './gql';

const AMBER = '#eaa827';

// Wire values match the GraphQL `InterviewQuestionPosition` enum members (uppercase).
// Deliberately declared locally, not imported from a shared file — this app has no
// GraphQL codegen, so every operation's result type is hand-written per-file (see the
// existing `InterviewQuestionSource` precedent in InterviewQuestionsTab.tsx).
type InterviewQuestionPosition = 'OPENING' | 'CLOSING';

interface TemplateQuestion {
  id: string;
  question: string;
  // Still selected by the query and still stored on the row, but deliberately not
  // surfaced or edited here: the only real consumer of difficulty is the AI
  // generation prompt on the job-order tab, which reads its own slider at call
  // time — never this stored value. Mutations below omit it so the server keeps
  // whatever the row already has.
  difficulty: number;
  sortOrder: number;
  position: InterviewQuestionPosition;
}

// Position pill styling — kept next to the type it describes so the two treatments
// (opening/closing) can't drift out of sync with the union above.
const POSITION_STYLES: Record<InterviewQuestionPosition, React.CSSProperties> = {
  OPENING: { background: '#e7f1ff', color: '#0d6efd', border: '1px solid #b6d4fe' },
  CLOSING: { background: '#e8f5e9', color: '#2e7d32', border: '1px solid #a5d6a7' },
};

const POSITION_LABELS: Record<InterviewQuestionPosition, string> = {
  OPENING: 'Opening',
  CLOSING: 'Closing',
};

// ── Single sortable row ───────────────────────────────────────────────────────
interface RowProps {
  question: TemplateQuestion;
  index: number;
  editingId: string | null;
  editingText: string;
  editingPosition: InterviewQuestionPosition;
  onEditTextChange: (text: string) => void;
  onEditPositionChange: (p: InterviewQuestionPosition) => void;
  onEditStart: (q: TemplateQuestion) => void;
  onEditSave: (id: string) => void;
  onEditCancel: () => void;
  onDelete: (id: string) => void;
}

const SortableRow: FC<RowProps> = ({
  question: q,
  index,
  editingId,
  editingText,
  editingPosition,
  onEditTextChange,
  onEditPositionChange,
  onEditStart,
  onEditSave,
  onEditCancel,
  onDelete,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: q.id,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    marginBottom: 12,
    padding: '12px 14px',
    border: `1px solid ${isDragging ? AMBER : '#dee2e6'}`,
    borderRadius: 6,
    background: isDragging ? '#fffbf0' : '#fff',
    boxShadow: isDragging ? '0 4px 12px rgba(0,0,0,0.12)' : 'none',
    zIndex: isDragging ? 999 : 'auto',
    position: 'relative',
  };

  const isEditing = editingId === q.id;

  return (
    <div ref={setNodeRef} style={style}>
      {/* Drag handle */}
      <div
        {...listeners}
        {...attributes}
        title="Drag to reorder"
        style={{
          cursor: 'grab', color: '#adb5bd', padding: '2px 4px', fontSize: 18,
          lineHeight: 1, userSelect: 'none', flexShrink: 0, alignSelf: 'center',
        }}
      >
        ⠿
      </div>

      <span style={{ minWidth: 26, fontWeight: 600, color: '#6c757d', paddingTop: 2, flexShrink: 0 }}>
        {index + 1}.
      </span>

      <div style={{ flex: 1 }}>
        {isEditing ? (
          <>
            <textarea
              value={editingText}
              onChange={(e) => onEditTextChange(e.target.value)}
              rows={3}
              style={{ width: '100%', border: '1px solid #80bdff', borderRadius: 4, padding: '6px 8px', resize: 'vertical' }}
              autoFocus
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 8, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <label htmlFor={`position-${q.id}`} style={{ margin: 0, fontSize: 13, fontWeight: 500 }}>Position:</label>
                <Input id={`position-${q.id}`} type="select" value={editingPosition}
                  onChange={(e) => onEditPositionChange(e.target.value as InterviewQuestionPosition)}
                  style={{ width: 120 }}>
                  <option value="OPENING">Opening</option>
                  <option value="CLOSING">Closing</option>
                </Input>
              </div>
            </div>
          </>
        ) : (
          <>
            <span style={{ lineHeight: 1.5 }}>{q.question}</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 4 }}>
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: 0.3,
                  padding: '2px 8px',
                  borderRadius: 10,
                  ...POSITION_STYLES[q.position],
                }}
              >
                {POSITION_LABELS[q.position]}
              </span>
            </div>
          </>
        )}
      </div>

      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
        {isEditing ? (
          <>
            <Button size="sm" color="success" onClick={() => onEditSave(q.id)}>Save</Button>
            <Button size="sm" color="secondary" onClick={onEditCancel}>Cancel</Button>
          </>
        ) : (
          <>
            <Button size="sm" color="outline-secondary" onClick={() => onEditStart(q)}>Edit</Button>
            <Button size="sm" color="outline-danger" onClick={() => onDelete(q.id)}>✕</Button>
          </>
        )}
      </div>
    </div>
  );
};

// ── Page ──────────────────────────────────────────────────────────────────────
const StarterInterviewQuestions = () => {
  const [questions, setQuestions] = useState<TemplateQuestion[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingText, setEditingText] = useState('');
  const [editingPosition, setEditingPosition] = useState<InterviewQuestionPosition>('OPENING');
  const [addingNew, setAddingNew] = useState(false);
  const [newText, setNewText] = useState('');
  const [newPosition, setNewPosition] = useState<InterviewQuestionPosition>('OPENING');

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  const { loading, refetch } = useQuery(STARTER_INTERVIEW_QUESTION_TEMPLATE, {
    fetchPolicy: 'cache-and-network',
    onCompleted: (data) => setQuestions(data?.starterInterviewQuestionTemplate ?? []),
  });

  const [addQuestion, { loading: adding }] = useMutation(ADD_STARTER_TEMPLATE_QUESTION, {
    onCompleted: (data) => {
      setQuestions((prev) => [...prev, data.addStarterTemplateQuestion]);
      setAddingNew(false);
      setNewText('');
      setNewPosition('OPENING');
    },
    onError: (err) => toast.error(err.message),
  });

  const [updateQuestion] = useMutation(UPDATE_STARTER_TEMPLATE_QUESTION, {
    onCompleted: (data) => {
      setQuestions((prev) =>
        prev.map((q) =>
          q.id === data.updateStarterTemplateQuestion.id ? { ...q, ...data.updateStarterTemplateQuestion } : q,
        ),
      );
      setEditingId(null);
    },
    onError: (err) => toast.error(err.message),
  });

  const [deleteQuestion] = useMutation(DELETE_STARTER_TEMPLATE_QUESTION, {
    onError: (err) => toast.error(err.message),
  });

  const [reorderQuestions] = useMutation(REORDER_STARTER_TEMPLATE_QUESTIONS, {
    onCompleted: (data) => setQuestions(data?.reorderStarterTemplateQuestions ?? []),
    onError: (err) => {
      toast.error(err.message);
      refetch();
    },
  });

  useEffect(() => {
    document.title = 'Starter Interview Questions - RealREPP';
  }, []);

  const handleSaveEdit = (id: string) => {
    if (!editingText.trim()) return;
    updateQuestion({
      variables: { id, question: editingText.trim(), position: editingPosition },
    });
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this starter question from the default template? Existing job orders are not affected.')) {
      return;
    }
    await deleteQuestion({ variables: { id } });
    setQuestions((prev) => prev.filter((q) => q.id !== id));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = questions.findIndex((q) => q.id === active.id);
    const newIndex = questions.findIndex((q) => q.id === over.id);
    const reordered = arrayMove(questions, oldIndex, newIndex);
    setQuestions(reordered);
    reorderQuestions({ variables: { orderedIds: reordered.map((q) => q.id) } });
  };

  return (
    <div className="admin-tabs team-tabs">
      <GoBack url={ADMIN_ROUTE} />

      <div className="primaryHeading">
        <h5 className="m-0">{STARTER_INTERVIEW_QUESTIONS}</h5>
      </div>

      <div style={{ padding: '16px 4px', maxWidth: 900 }}>
        <p style={{ color: '#6c757d', fontSize: 13 }}>
          These are the default starter interview questions copied onto every new job order and applied when a
          recruiter clicks <strong>Restore starter questions</strong>. Drag to reorder. Opening questions are asked
          first, then the job-specific AI questions, then the closing questions. Changing a question&apos;s position
          here affects new job orders and any job order where a recruiter clicks Restore starter questions — it does
          not change job orders that already exist.
        </p>

        {loading && questions.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40 }}><Spinner /></div>
        ) : (
          <>
            {questions.length === 0 && !addingNew && (
              <p style={{ color: '#6c757d', fontStyle: 'italic' }}>
                No starter questions in the template. Add one below.
              </p>
            )}

            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={questions.map((q) => q.id)} strategy={verticalListSortingStrategy}>
                {questions.map((q, index) => (
                  <SortableRow
                    key={q.id}
                    question={q}
                    index={index}
                    editingId={editingId}
                    editingText={editingText}
                    editingPosition={editingPosition}
                    onEditTextChange={setEditingText}
                    onEditPositionChange={setEditingPosition}
                    onEditStart={(item) => {
                      setEditingId(item.id);
                      setEditingText(item.question);
                      setEditingPosition(item.position);
                    }}
                    onEditSave={handleSaveEdit}
                    onEditCancel={() => setEditingId(null)}
                    onDelete={handleDelete}
                  />
                ))}
              </SortableContext>
            </DndContext>

            {/* Add new */}
            {addingNew ? (
              <div style={{
                display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 12,
                padding: '12px 14px', border: '1px dashed #80bdff', borderRadius: 6, background: '#f0f7ff',
              }}>
                <div style={{ flex: 1 }}>
                  <textarea
                    value={newText}
                    onChange={(e) => setNewText(e.target.value)}
                    placeholder="Type the starter question here..."
                    rows={3}
                    style={{ width: '100%', border: '1px solid #80bdff', borderRadius: 4, padding: '6px 8px', resize: 'vertical' }}
                    autoFocus
                  />
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 8, flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <label htmlFor="new-question-position" style={{ margin: 0, fontSize: 13, fontWeight: 500 }}>Position:</label>
                      <Input id="new-question-position" type="select" value={newPosition}
                        onChange={(e) => setNewPosition(e.target.value as InterviewQuestionPosition)}
                        style={{ width: 120 }}>
                        <option value="OPENING">Opening</option>
                        <option value="CLOSING">Closing</option>
                      </Input>
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <Button size="sm" color="success" disabled={adding} onClick={() => {
                    if (!newText.trim()) return;
                    addQuestion({ variables: { question: newText.trim(), position: newPosition } });
                  }}>Save</Button>
                  <Button size="sm" color="secondary" onClick={() => { setAddingNew(false); setNewText(''); setNewPosition('OPENING'); }}>Cancel</Button>
                </div>
              </div>
            ) : (
              <Button style={{ borderColor: AMBER, color: AMBER }} outline size="sm" onClick={() => setAddingNew(true)}>
                + Add Starter Question
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default StarterInterviewQuestions;
